import { createContext, ReactElement, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetSupportPlanResponse } from '@twiliointernal/e2-commons-shared-definitions';
import { supportPlansViewTypes } from '@twiliointernal/services-common/containers/SupportPlansView';

import { constants, types } from './definitions';
import { isPersonalizedSupport } from './utils';
import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import { useUserDetails } from '../../hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

export const SupportPlanContext =
  createContext<types.SupportPlanProviderContext>(constants.initialValue);

const fetchSupportPlanFnKey = 'support-plan-key';

export function SupportPlanProvider({ children }): ReactElement {
  const [isLoading, toggleIsLoading] = useState<boolean>(true);
  const { account, isLoggedIn, isLoading: isLoadingUser } = useUserDetails();

  const queryKey = [fetchSupportPlanFnKey];

  async function fetchSupportPlan(): Promise<GetSupportPlanResponse> {
    const {
      data: supportPlans,
      errorCode,
      message,
      success,
    } = await SupportApi.getSupportPlan();

    toggleIsLoading(false);

    if (!success) {
      const errorMessage = `${constants.LOG_PREFIX}::${errorCode}`;
      logger.error(errorMessage, { message });
      throw Error(errorMessage);
    }

    return supportPlans;
  }

  useEffect(() => {
    // User is not logged in or doesn't have access
    if (!isLoadingUser && !isLoggedIn) {
      toggleIsLoading(false);
    }

    // User is logged in and has access
    if (!isLoadingUser && isLoggedIn) {
      refetchSupportPlan();
    }
  }, [isLoadingUser, isLoggedIn, account?.sid]);

  const {
    isError,
    data: supportPlan,
    refetch: refetchSupportPlan,
  } = useQuery({
    retry: false,
    queryKey,
    queryFn: () => fetchSupportPlan(),
    enabled: false,
  });

  const hasPersonalizedSupportPlan =
    !!supportPlan?.currentPlan?.billableItemSid &&
    isPersonalizedSupport(supportPlan.currentPlan.billableItemSid);
  const hasDeveloperSupport =
    !!supportPlan?.currentPlan?.billableItemSid &&
    supportPlan.currentPlan.billableItemSid ===
      supportPlansViewTypes.SupportPlanBillableItemSids.developer;

  const contextValue: types.SupportPlanProviderContext = {
    ...constants.initialValue,
    loading: isLoading,
    hasError: isError,
    hasDeveloperSupport,
    hasPersonalizedSupportPlan,
    supportPlan: supportPlan?.currentPlan,
    pendingPlan: supportPlan?.pendingPlan,
  };
  return (
    <SupportPlanContext.Provider value={contextValue}>
      {children}
    </SupportPlanContext.Provider>
  );
}
